import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { OverlayPanel } from "primereact/overlaypanel";
import { FormEvent } from "primereact/ts-helpers";
import { UserRole } from "../../../models/enums/user-role.enum";
import { Employee } from "../../../models/employee.interface";
import { Shift } from "../../../models/shift.interface";
import { HiOutlineUserCircle } from "react-icons/hi";
import {
    retrieveAvailableWorkersBetweenDates,
    retrieveShiftsWeekByDate,
    retrieveWorkersByDate
} from "../../../services/data-manager/data-manager.service";
import { WorkersByDateRequest } from "../../../services/backend/api/shifts/requests/workers-by-date.request";
import {
    AvailableWorkersBetweenDatesRequest
} from "../../../services/backend/api/shifts/requests/available-workers-between-dates.request";
import { updateCalendar } from "../../../services/backend/api/shifts/shifts.apis";
import { UpdateCalendarRequest } from "../../../services/backend/api/shifts/requests/update-calendar.request";
import { ShiftItem } from "../../../services/backend/api/shifts/models/shift-item.interface";
import { formatDate, tomorrow, yesterday } from "../../../utils/date.utils";
import { useToastContext } from "../../../contexts/ToastContext";
import FullCalendar from '@fullcalendar/react'
import interactionPlugin from '@fullcalendar/interaction'
import { Calendar } from "primereact/calendar";
import { InputText } from "primereact/inputtext";
import { MultiSelect } from "primereact/multiselect";
import { Dropdown } from "primereact/dropdown";
import { Button } from "primereact/button";
import { FaRegEdit, FaRegTrashAlt } from "react-icons/fa";
import resourceTimelinePlugin from '@fullcalendar/resource-timeline';
import listPlugin from '@fullcalendar/list';
import { simplifiedId } from "../../../utils/text-formatting.utils";
import i18n from "i18next";
import { mergeArrays } from "../../../utils/generic.utils";
import Loader from "../../common/LoaderComponent";
import FullScreenLoader from "../../common/FullScreenLoader";
import SupervisorIcon from '../../../assets/icons/supervisor.svg';
import OperatorIcon from '../../../assets/icons/operator.svg';

const FULLCALENDAR_API_KEY = process.env.REACT_APP_FULLCALENDAR_LICENSE_KEY

interface WeeklyCalendarProps {
    onChange: (date: Date) => void;
    onVisibleShiftsChange: (shifts: Shift[]) => void;
    onAvailableEmployeesChange: (employees: Employee[]) => void;
    onSaveShifts?: (shifts: Shift[]) => void;
    selectedDate: Date;
    onSelectDateChange: (date: Date) => void;
}

//const WEEK_DAYS = 7;
const START_HOUR = 0;
const END_HOUR = 23;
const STEP_MINUTE = 15;
const NEW_SHIFT = '- New Shift';

const WeeklyCalendar: React.FC<WeeklyCalendarProps> = ({
    onChange,
    onVisibleShiftsChange,
    onAvailableEmployeesChange,
    onSaveShifts,
    selectedDate,
    onSelectDateChange
}) => {
    const { t } = useTranslation();
    const [isLoading, setIsLoading] = useState(true);
    const [isDeleteLoading, setIsDeleteLoading] = useState(false);
    const [isDateLoading, setIsDateLoading] = useState(false);
    const [isSavingShift, setIsSavingShift] = useState(false);
    const { successToast, errorToast } = useToastContext();

    const [startTime, setStartTime] = useState<Date>(new Date());
    const [endTime, setEndTime] = useState<Date>(new Date());

    const [minSelectableDate, setMinSelectableDate] = useState<Date | undefined>();
    const [maxSelectableDate, setMaxSelectableDate] = useState<Date | undefined>();
    const [endTimeMaxDate, setEndTimeMaxDate] = useState<Date | undefined>();

    const [selectedLocation, setSelectedLocation] = useState<string>();
    const [selectedSupervisor, setSelectedSupervisor] = useState<Employee>();
    const [selectedOperators, setSelectedOperators] = useState<Employee[]>();
    const [availableSupervisors, setAvailableSupervisors] = useState<Employee[]>([]);
    const [availableOperators, setAvailableOperators] = useState<Employee[]>([]);

    const [availableShifts, setAvailableShifts] = useState<Shift[]>([]);
    const [visibleShifts, setVisibleShifts] = useState<Shift[]>([]);

    const [selectedShift, setSelectedShift] = useState<Shift>();

    const [shiftOperatorsVisible, setShiftOperatorsVisible] = useState<Employee[]>([]);

    const assignShiftOverlayPanel = useRef<OverlayPanel>(null)
    const shiftOperatorsOverlayPanel = useRef<OverlayPanel>(null)
    const locale = i18n.language === "ar" ? "ar-SA" : "en-US";

    const [overlayCoords, setOverlayCoords] = useState<{ x: number, y: number } | null>(null);
    const overlayDivRef = useRef<HTMLDivElement>(null);

    const goToDateOverlayPanel = useRef<OverlayPanel>(null);
    const [goToDate, setGoToDate] = useState<Date | null>(null);

    const handleShiftSelection = (shift: Shift) => {
        setSelectedShift(shift);
        setStartTime(shift.startDate);
        setEndTime(shift.endDate);
        setSelectedSupervisor(shift.supervisor);
        setSelectedOperators(shift.operators);
        setSelectedLocation(shift.place);
    }

    const handleNewShiftForm = () => {
        setSelectedShift(undefined);
        setSelectedSupervisor(undefined);
        setSelectedOperators([]);
        setSelectedLocation(undefined);
    }

    const showAssignShiftOverlayPanel = (e: any, shiftId?: string) => {
        if (shiftId) {
            const shift = availableShifts.find(shift => shift.id === shiftId);
            if (shift) {
                handleShiftSelection(shift);
            }
        } else {
            handleNewShiftForm();
        }
        const x = e?.jsEvent?.pageX || e?.nativeEvent?.pageX || e?.pageX;
        const y = e?.jsEvent?.pageY || e?.nativeEvent?.pageY || e?.pageY;
        if (x && y) {
            setOverlayCoords({ x, y });
            setTimeout(() => {
                if (overlayDivRef.current) {
                    assignShiftOverlayPanel.current?.toggle(e, overlayDivRef.current);
                }
            }, 0);
        } else {
            assignShiftOverlayPanel.current?.toggle(e);
        }
    };

    const onHideAssignShiftOverlayPanel = () => {
        setOverlayCoords(null);
        setStartTime(new Date(selectedDate));
        setEndTime(new Date(selectedDate));
    };

    const onlyHourDate = (date: Date): Date => {
        const newDate = new Date(date);
        newDate.setMinutes(0);
        newDate.setSeconds(0);
        return newDate;
    }

    const handleStartTimeChange = (e: FormEvent<(Date | null)>) => {
        if (e.value) {
            setStartTime(e.value);
            if (endTime && e.value > endTime) {
                setEndTime(e.value);
            }
        }
    };
    
    const handleEndTimeChange = (e: FormEvent<(Date | null)>) => {
        if (e.value) {
            setEndTime(e.value);
        }
    };

    const handleSupervisorSelection = (e: any) => {
        setSelectedSupervisor(e.value);
        setSelectedOperators([]);
    };

    const saveShift = () => {
        if (isSavingShift) {
            return;
        }
        if (!startTime || !endTime || !selectedSupervisor || !selectedOperators || startTime >= endTime) {
            errorToast(t('messages.calendarUpdatedError'));
            return;
        }
        let isUpdate = false;
        let shift: Shift;
        if (selectedShift !== undefined) {
            isUpdate = true;
            shift = selectedShift;
        } else {
            shift = { id: "" } as Shift;
        }
        shift.startDate = startTime;
        shift.endDate = endTime;
        shift.supervisor = selectedSupervisor;
        shift.operators = selectedOperators;

        for (let i = 0; i < shift.endDate.getHours() - shift.startDate.getHours(); i++) {
            if (isSlotAssigned(shift.startDate.getHours() + i, shift.startDate.getDay())) {
                assignShiftOverlayPanel.current?.hide();
                return;
            }
        }
        const newShifts: ShiftItem[] = []
        const shiftsItems: ShiftItem[] = []
        const employees = selectedOperators.concat(selectedSupervisor);
        for (let i = 0; i < employees.length; i++) {
            const employee = employees[i];
            let employeeShiftId = undefined
            if (isUpdate) {
                if (employee.type === UserRole.Supervisor) {
                    employeeShiftId = shift.id;
                } else {
                    employeeShiftId = employee.shifts?.find(s => s.supervisorShiftId === shift.id)?.shiftId;
                }
            }
            const shiftItem: ShiftItem = {
                shiftId: employeeShiftId,
                proposal: {},
                endDate: formatDate(shift.endDate) || '',
                label: 'Work',
                proposalStatus: 'false',
                userStart: '',
                startTime: shift.startDate.toISOString(),
                endTime: shift.endDate.toISOString(),
                userEnd: '',
                userId: employee.id,
                startDate: formatDate(shift.startDate) || '',
                place: selectedLocation || '',
            }

            if (isUpdate && !employeeShiftId) {
                newShifts.push(shiftItem);
            } else {
                shiftsItems.push(shiftItem);
            }

        }

        const request: UpdateCalendarRequest = {
            shiftsAdd: !isUpdate ? shiftsItems : newShifts,
            shiftsUpdate: isUpdate ? shiftsItems : [],
            shiftsDelete: [],
            updateInfoForAddNewOperators: newShifts.length > 0
                ? {
                    supervisorId: shift.supervisor.id,
                    supervisorShiftId: shift.supervisor.shifts?.find(s => s.shiftId === shift.id)?.shiftId || ''
                }
                : undefined
        }
        setIsSavingShift(true)
        updateCalendar(request).then((response) => {
            setSelectedShift(undefined);
            assignShiftOverlayPanel.current?.hide();
            retrieveShiftsWeekByDate({ date: selectedDate }).then(shifts => {
                const filteredShifts = getSelectedDateShifts(shifts, selectedDate);
                setAvailableShifts(filteredShifts);
                if (response.notAdded.length > 0 || response.notUpdated.length > 0) {
                    errorToast(t('messages.calendarUpdatedError'));
                } else {
                    successToast(t('messages.calendarUpdatedSuccess'));
                    if (onSaveShifts) {
                        onSaveShifts(filteredShifts);
                    }
                }
            })
        }).catch(() => {
            errorToast(t('messages.calendarUpdatedError'));
        }).finally(() => {
            setIsSavingShift(false)
        });
    }

    const deleteShift = async () => {
        try {
            console.log("Stiamo per cancellare il turno");
            if (!selectedShift) {
                errorToast(t('messages.calendarUpdatedError'));
                return;
            }
            setIsDeleteLoading(true);
            
            const shiftsToDelete: string[] = [selectedShift?.id]
            if (selectedShift.supervisor.shifts) {
                for (const shift of selectedShift.supervisor.shifts) {
                    const startDate = new Date(shift.startTime);
                    if (startDate >= selectedShift.startDate) {
                        shiftsToDelete.push(shift.shiftId);
                    }
                }
            }
            for (const operator of selectedShift.operators) {
                if (operator.shifts) {
                    for (const shift of operator.shifts) {
                        const startDate = new Date(shift.startTime);
                        if (startDate >= selectedShift.startDate) {
                            shiftsToDelete.push(shift.shiftId);
                        }
                    }
                }
            }

            const request: UpdateCalendarRequest = {
                shiftsAdd: [],
                shiftsUpdate: [],
                shiftsDelete: shiftsToDelete
            }

            updateCalendar(request).then((response) => {
                setSelectedShift(undefined);
                assignShiftOverlayPanel.current?.hide();
                retrieveShiftsWeekByDate({ date: selectedDate }).then(shifts => {
                    const filteredShifts = getSelectedDateShifts(shifts, selectedDate);
                    setAvailableShifts(filteredShifts);
                    if (response.notUpdated.length > 0) {
                        errorToast(t('messages.calendarUpdatedError'));
                    } else {
                        successToast(t('messages.calendarUpdatedSuccess'));
                        onSaveShifts?.(filteredShifts);
                    }
                    setIsDeleteLoading(false);
                })
            }).catch(() => {
                errorToast(t('messages.calendarUpdatedError'));
                setIsDeleteLoading(false);
            });
        } catch (error) {
            console.error("Errore generale nel cancellare il turno:", error);
            setIsDeleteLoading(false);
        }
    }

    const getAssignedShift = (hour: number, day: number): Shift | undefined => {
        const actualDate = new Date(selectedDate);
        actualDate.setDate(actualDate.getDate() + day);
        actualDate.setHours(START_HOUR + hour);

        return availableShifts.find(shift => {
            return actualDate >= shift.startDate && actualDate < shift.endDate
        });
    }

    const getShiftById = (id: string): Shift | undefined => {
        return availableShifts.find(shift => shift.id === id);
    }

    const isSlotAssigned = (hour: number, day: number) => {
        return getAssignedShift(hour, day) !== undefined;
    }

    const selectedEmployeeItem = (option: Employee) => {
        return (
            option && (
                <div className="flex items-center justify-start px-1 rounded"
                    style={{
                        backgroundColor: option?.isOnLeave || (option?.supervisorId && option?.supervisorId !== selectedSupervisor?.id)
                            ? 'green'
                            : 'bg-transparent',
                        opacity: option?.isOnLeave || option?.assigned || (option?.supervisorId && option?.supervisorId !== selectedSupervisor?.id)
                            ? 0.5
                            : 1
                    }}
                >
                    <div>{option?.name} {option?.surname}</div>
                </div>
            )
        );
    };

    const showShiftOperators = (e: any, operators: Employee[]) => {
        setShiftOperatorsVisible(operators)
        shiftOperatorsOverlayPanel.current?.toggle(e)
        e.stopPropagation()
    }

    const hideShiftOperators = () => {
        setShiftOperatorsVisible([])
        shiftOperatorsOverlayPanel.current?.hide()
    }

    const getDayResources = (): any[] => {
        const resources = availableShifts.map(shift => {
            return {
                id: shift.id,
                title: `${shift.startDate.toLocaleString(locale, {
                    day: "2-digit",
                    month: "2-digit"
                })} - ${simplifiedId(shift.id)}`,
            }
        })

        resources.push({
            id: NEW_SHIFT,
            title: NEW_SHIFT
        })

        return resources
    }

    const getCalendarEvent = (): any[] => {
        const calendarShifts = availableShifts.map(shift => {
            return {
                title: shift.id,
                resourceId: shift.id,
                start: shift.startDate,
                end: shift.endDate,
                allDay: false
            }
        });
        if (startTime !== undefined && endTime !== undefined && startTime < endTime && selectedShift === undefined) {
            calendarShifts.push({
                title: NEW_SHIFT,
                resourceId: NEW_SHIFT,
                start: startTime,
                end: endTime,
                allDay: false
            })
        }
        return calendarShifts;
    }

    const getSelectedDateShifts = useCallback((shifts: Shift[], targetDate: Date): Shift[] => {
        // Imposta l'inizio e la fine della giornata di riferimento
        const dayStart = new Date(targetDate);
        dayStart.setHours(0, 0, 0, 0);
    
        const dayEnd = new Date(targetDate);
        dayEnd.setHours(23, 59, 59, 999);
    
        return shifts.filter(shift => {
            const shiftStart = new Date(shift.startDate);
            const shiftEnd = new Date(shift.endDate);
    
            // Un turno è visibile in un dato giorno se il suo intervallo di tempo
            // si sovrappone con l'intervallo di quel giorno.
            const overlaps = shiftStart <= dayEnd && shiftEnd >= dayStart;
            return overlaps;
        });
    }, []);

    useEffect(() => {
        setIsLoading(true)
        const request: AvailableWorkersBetweenDatesRequest = {
            startTime: startTime,
            endTime: endTime
        }
        // TODO: remove this line when the backend is ready
        request.endTime.setMinutes(request.endTime.getMinutes() - 1);
        retrieveAvailableWorkersBetweenDates(request).then((employees) => {
            setAvailableSupervisors(employees.filter(employee => employee.type === UserRole.Supervisor));
            setAvailableOperators(employees.filter(employee => employee.type === UserRole.Operator));
            onAvailableEmployeesChange(employees)
            setIsLoading(false)
        });
    }, [endTime, startTime, onAvailableEmployeesChange]);

    useEffect(() => {
        setIsDateLoading(true);
        const weekday = onlyHourDate(selectedDate);
        setStartTime(weekday);
        setEndTime(weekday);

        const request: WorkersByDateRequest = {
            dateDate: selectedDate
        };
        retrieveWorkersByDate(request).then((employees) => {
            setAvailableSupervisors(employees.filter(employee => employee.type === UserRole.Supervisor));
            setAvailableOperators(employees.filter(employee => employee.type === UserRole.Operator));
        });
        retrieveShiftsWeekByDate({ date: selectedDate }).then(shifts => {
            const filteredShifts = getSelectedDateShifts(shifts, selectedDate);
            setAvailableShifts(filteredShifts);
            setIsDateLoading(false);
        }).catch((error) => {
            console.error("Errore nel caricamento dei turni:", error);
            setIsDateLoading(false);
        });
    }, [getSelectedDateShifts, selectedDate]);

    useEffect(() => {
        setVisibleShifts(availableShifts.filter(
            shift => shift.startDate >= yesterday() && shift.endDate < tomorrow())
        );
    }, [availableShifts]);

    useEffect(() => {
        onChange(selectedDate);
    }, [onChange, selectedDate]);

    useEffect(() => {
        onVisibleShiftsChange(visibleShifts);
    }, [onVisibleShiftsChange, visibleShifts]);

    useEffect(() => {
        console.log("WeeklyCalendar - isLoading changed:", isLoading);
    }, [isLoading]);

    useEffect(() => {
        console.log("WeeklyCalendar - isDateLoading changed:", isDateLoading);
    }, [isDateLoading]);

    useEffect(() => {
        console.log("WeeklyCalendar - isDeleteLoading changed:", isDeleteLoading);
    }, [isDeleteLoading]);

    // Sincronizza il calendario FullCalendar quando cambia la prop selectedDate
    useEffect(() => {
        if (selectedDate && calendarRef.current) {
            const calendarApi = calendarRef.current.getApi();
            if (calendarApi) {
                const currentDate = calendarApi.getDate();
                if (currentDate.toDateString() !== selectedDate.toDateString()) {
                    console.log("WeeklyCalendar - Syncing calendar with selectedDate:", selectedDate);
                    calendarApi.gotoDate(selectedDate);
                }
            }
        }
    }, [selectedDate]);

    useEffect(() => {
        const baseDate = selectedDate || new Date();
        const minDate = new Date(baseDate);
        minDate.setDate(baseDate.getDate() - 1);
        minDate.setHours(0, 0, 0, 0);
        setMinSelectableDate(minDate);

        const maxDate = new Date(baseDate);
        maxDate.setDate(baseDate.getDate() + 1);
        maxDate.setHours(23, 59, 59, 999);
        setMaxSelectableDate(maxDate);
    }, [selectedDate]);

    useEffect(() => {
        if (startTime) {
            const newMaxEndDate = new Date(startTime);
            newMaxEndDate.setHours(newMaxEndDate.getHours() + 24);

            if (maxSelectableDate && newMaxEndDate > maxSelectableDate) {
                setEndTimeMaxDate(maxSelectableDate);
            } else {
                setEndTimeMaxDate(newMaxEndDate);
            }
        }
    }, [startTime, maxSelectableDate]);

    const ShiftItem: React.FC<{ shift?: Shift }> = ({ shift }) => {
        return (
            <>
                {!shift
                    ? <div></div>
                    :
                    <div className="flex items-center gap-4 p-2 whitespace-nowrap">
                        {/* Supervisor Section */}
                        <div className="flex items-center gap-2 shrink-0">
                            <p className="font-semibold">{t('weeklyCalendar.supervisor')}</p>
                            <img src={SupervisorIcon} alt="supervisor" className="w-6 h-6 shrink-0" />
                        </div>

                        {/* Time Section */}
                        <div className="font-mono text-center flex-grow">
                            {shift.startDate.toLocaleString('en-GB', { hour: '2-digit' })}
                            {' - '}
                            {shift.endDate.toLocaleString('en-GB', { hour: '2-digit' })}
                        </div>

                        {/* Operators Section */}
                        <div className="flex items-center gap-2 shrink-0">
                            <p className="font-semibold">{`${t('weeklyCalendar.operators')} ${shift.operators.length}`}</p>
                            <div className="cursor-pointer" onClick={(e) => showShiftOperators(e, shift.operators)}>
                                <img src={OperatorIcon} alt="operator" className="w-6 h-6 shrink-0" />
                            </div>
                        </div>

                        <OverlayPanel
                            ref={shiftOperatorsOverlayPanel}
                            showCloseIcon
                            onHide={hideShiftOperators}
                            onClick={e => e.stopPropagation()}
                            className={`primary-container p-2 border-4 translate-y-[32rem]`}
                        >
                            <div
                                className="flex flex-col justify-center items-center p-4 space-y-2 w-full text-[var(--text-color)]">
                                <div className="flex items-center justify-center">
                                    <p className="text-lg font-semibold">{t('weeklyCalendar.operators')}</p>
                                </div>
                                {
                                    shiftOperatorsVisible.map((operator, index) => {
                                        return (
                                            <div key={index} className="flex items-center justify-center">
                                                <div>{operator?.name} {operator?.surname}</div>
                                            </div>
                                        );
                                    })
                                }
                            </div>
                        </OverlayPanel>
                    </div>
                }
            </>
        );
    }

    const setShiftDate = (start: Date, end: Date) => {
        setStartTime(start);
        setEndTime(end);
        saveShift();
    }

    const handleDateSelect = (selectInfo: any) => {
        setStartTime(selectInfo.start);
        setEndTime(selectInfo.end);
        showAssignShiftOverlayPanel(selectInfo.jsEvent);
    }

    const handleEventClick = (clickInfo: any) => {
        showAssignShiftOverlayPanel(clickInfo.jsEvent, clickInfo.event.title);
    }

    const handleEvents = (events: any) => {

    }

    const handleEventChange = (event: any) => {
        const shift = getAssignedShift(event.oldEvent.start.getHours(), event.oldEvent.start.getDay());
        if (!shift) {
            return;
        }
        handleShiftSelection(shift);
        setShiftDate(event.event.start, event.event.end);
    }

    const renderEventContent = (eventInfo: any) => {
        return (
            <div className="p-2 overflow-auto bg-[var(--primary-color)] cursor-pointer rounded-[var(--border-radius)]">
                <ShiftItem shift={getShiftById(eventInfo.event.title)} />
            </div>
        )
    }

    const onDateChange = (event: any) => {
        if (!event.start) {
            return;
        }
        const newDate = new Date(event.start);
        if (newDate.toDateString() === selectedDate.toDateString()) {
            return;
        }
        onSelectDateChange(newDate);
    }

    const handleGoToDate = (date: Date | null) => {
        if (date) {
            const calendarApi = calendarRef.current?.getApi();
            if (calendarApi) {
                calendarApi.gotoDate(date);
                onSelectDateChange(date);
                goToDateOverlayPanel.current?.hide();
            }
        }
    };

    const calendarRef = useRef<any>(null);

    return (
        <>
            <FullScreenLoader isLoading={isDeleteLoading || isSavingShift} />
            {overlayCoords && (
                <div
                    ref={overlayDivRef}
                    style={{
                        position: 'absolute',
                        left: overlayCoords.x,
                        top: overlayCoords.y - 80,
                        width: 1,
                        height: 1,
                        pointerEvents: 'none',
                        zIndex: 9999
                    }}
                />
            )}
            <div className="relative">
                {isDateLoading && (
                    <div className="absolute inset-0 flex items-center justify-center backdrop-blur-sm z-50 rounded-lg">
                        <Loader />
                    </div>
                )}
                <FullCalendar
                    ref={calendarRef}
                    height={680}
                    plugins={[interactionPlugin, resourceTimelinePlugin, listPlugin]}
                    schedulerLicenseKey={FULLCALENDAR_API_KEY}
                    customButtons={{
                        newShiftButton: {
                            text: t('weeklyCalendar.newShift'),
                            click: (e) => showAssignShiftOverlayPanel(e),
                        },
                        goToDateButton: {
                            text: t('weeklyCalendar.goToDate'),
                            click: (e: any) => {
                                if (goToDateOverlayPanel.current) {
                                    goToDateOverlayPanel.current.toggle(e);
                                }
                            }
                        }
                    }}
                    buttonText={{
                        today: t('weeklyCalendar.today')
                    }}
                    headerToolbar={{
                        left: 'prev,next today goToDateButton',
                        center: 'title',
                        right: 'newShiftButton'
                    }}
                    resourceAreaHeaderContent='Shifts'
                    resourceOrder='title'
                    defaultAllDay={false}
                    initialView='resourceTimelineDay'
                    editable={false}
                    selectable={true}
                    selectMirror={true}
                    dayMaxEvents={true}
                    weekends={true}
                    datesSet={onDateChange}
                    resources={getDayResources()}
                    events={getCalendarEvent()}
                    select={handleDateSelect}
                    eventContent={renderEventContent}
                    eventClick={handleEventClick}
                    eventsSet={handleEvents}
                    eventAdd={() => {
                        console.log('eventAdd');
                    }}
                    eventChange={(event) => {
                        handleEventChange(event);
                    }}
                    loading={(isLoading) => {
                        setIsLoading(isLoading);
                    }}
                />
            </div>
            <div className="py-2 px-6 scroll-bar-hidden absolute top-6 left-[20%]">
                <OverlayPanel ref={assignShiftOverlayPanel}
                    showCloseIcon
                    onHide={onHideAssignShiftOverlayPanel}
                    className={`primary-container p-0 min-w-[18rem] min-h-[20rem] border-4
                                                ${selectedShift ? 'border-white' : 'border-yellow-500'}
                                              `}
                >
                    <div
                        className="flex flex-col justify-center items-center p-4 space-y-2 w-[18rem] text-[var(--text-color)]">
                        <div className="w-full space-y-1">
                            <div>{t('weeklyCalendar.pickStartTime')}</div>
                            <Calendar className="w-full"
                                showTime
                                selectionMode="single"
                                value={startTime}
                                onChange={handleStartTimeChange}
                                minDate={minSelectableDate}
                                maxDate={maxSelectableDate}
                                stepMinute={STEP_MINUTE}
                                placeholder={t('weeklyCalendar.pickStartTime')}
                            />
                        </div>
                        <div className="w-full space-y-1">
                            <div>{t('weeklyCalendar.pickEndTime')}</div>
                            <Calendar className="w-full"
                                showTime
                                selectionMode="single"
                                value={endTime}
                                minDate={startTime}
                                maxDate={endTimeMaxDate}
                                onChange={handleEndTimeChange}
                                stepMinute={STEP_MINUTE}
                                placeholder={t('weeklyCalendar.pickEndTime')}
                            />
                        </div>
                        <div className="w-full space-y-1">
                            <div>{t('weeklyCalendar.selectSupervisor')}</div>
                            <Dropdown className="w-full min-h-11"
                                value={selectedSupervisor}
                                options={[...availableSupervisors, selectedSupervisor].filter(Boolean)}
                                optionLabel="id"
                                placeholder={t('weeklyCalendar.selectSupervisor')}
                                checkmark={true} highlightOnSelect={false}
                                onChange={(e) => handleSupervisorSelection(e)}
                                itemTemplate={selectedEmployeeItem}
                                valueTemplate={selectedEmployeeItem}
                            />
                        </div>
                        <div className="w-full space-y-1">
                            <div>{t('weeklyCalendar.selectOperators')}</div>
                            <MultiSelect className="w-full min-h-11"
                                value={selectedOperators}
                                onChange={(e) => setSelectedOperators(e.value)}
                                options={mergeArrays(selectedOperators || [], availableOperators)}
                                optionLabel="id"
                                optionDisabled={(option) => option.supervisorId !== selectedSupervisor?.id}
                                placeholder={t('weeklyCalendar.selectOperators')}
                                itemTemplate={selectedEmployeeItem}
                                selectedItemTemplate={selectedEmployeeItem}
                            />
                        </div>
                        <div className="w-full space-y-1">
                            <div>{t('weeklyCalendar.location')}</div>
                            <InputText value={selectedLocation} className="w-full"
                                placeholder={t('weeklyCalendar.location')}
                                onChange={(e) => setSelectedLocation(e.target.value)}
                            />
                        </div>
                        <div className="w-full space-y-2 pt-1">
                            <Button className="space-x-1 h-8" onClick={saveShift}>
                                <FaRegEdit size={20}></FaRegEdit>
                                {
                                    !selectedShift
                                        ? <p>{t('weeklyCalendar.save')}</p>
                                        : <p>{t('weeklyCalendar.edit')}</p>
                                }
                            </Button>

                            {
                                selectedShift &&
                                <Button className="space-x-1 h-8" onClick={() => deleteShift()}
                                    style={{ borderColor: "darkred" }}
                                >
                                    <FaRegTrashAlt size={20}></FaRegTrashAlt>
                                    {
                                        <p>{t('weeklyCalendar.delete')}</p>
                                    }
                                </Button>
                            }
                        </div>
                    </div>
                </OverlayPanel>
            </div>
            <OverlayPanel ref={goToDateOverlayPanel}
                showCloseIcon
                className="primary-container p-4 border-4 border-white"
            >
                <div className="flex flex-col justify-center items-center space-y-2 text-[var(--text-color)]">
                    <div>{t('weeklyCalendar.selectDate')}</div>
                    <Calendar
                        value={goToDate}
                        onChange={(e) => handleGoToDate(e.value as Date)}
                        inline
                        selectionMode="single"
                    />
                </div>
            </OverlayPanel>
        </>
    );
}

export default WeeklyCalendar;